import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Download } from 'lucide-react';

interface PluginDisplay {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  iconImage: string;
  color: string;
}

const PluginShowcase = () => {
  const pluginsDisplay: PluginDisplay[] = [
    {
      id: '1',
      slug: 'activity-monitor-pro',
      name: 'Activity Monitor Pro',
      description: 'Complete WordPress activity tracking with one-click undo system. Track 450+ activities, detect anomalies with AI, and never lose a change again.',
      category: 'Security/Monitoring',
      iconImage: '/lovable-uploads/activity-monitor-pro-icon.jpg',
      color: 'from-green-500 to-emerald-600',
    }
  ];

  return (
    <section id="plugin-showcase" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
            Our WordPress Plugin
          </h2>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            Premium AI-powered WordPress plugin designed to enhance your site's security and give you complete control over every change.
          </p>
        </div>

        <div className="flex justify-center max-w-xl mx-auto">
          {pluginsDisplay.map((plugin) => (
            <Card
              key={plugin.id}
              className="bg-luxury-900 border-luxury-700 overflow-hidden hover:border-gold/50 transition-all duration-300 hover:scale-105 group w-full"
            >
              {/* Plugin Icon Header */}
              <div className={`h-48 bg-gradient-to-br ${plugin.color} flex items-center justify-center relative overflow-hidden`}>
                <div className="absolute inset-0 bg-black/30" />
                <img
                  src={plugin.iconImage}
                  alt={plugin.name}
                  className="w-32 h-32 rounded-full object-cover relative z-10 shadow-2xl border-4 border-white/20"
                />
              </div>

              {/* Plugin Content */}
              <div className="p-8">
                <Badge className="mb-3 bg-gold/10 text-gold border-gold/20">
                  {plugin.category}
                </Badge>

                <h3 className="text-2xl font-bold mb-3 text-white">
                  {plugin.name}
                </h3>

                <p className="text-white/70 mb-6 leading-relaxed text-lg">
                  {plugin.description}
                </p>

                <div className="flex gap-3">
                  <Button
                    size="lg"
                    asChild
                    className="flex-1"
                  >
                    <Link to={`/wordpress-plugins/${plugin.slug}`} onClick={() => window.scrollTo(0, 0)}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Details
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    asChild
                  >
                    <Link to={`/wordpress-plugins/${plugin.slug}`} onClick={() => window.scrollTo(0, 0)}>
                      <Download className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Coming Soon Section */}
        <div className="mt-16 text-center">
          <p className="text-white/50 text-lg">
            More AI-powered plugins coming soon. Stay tuned!
          </p>
        </div>
      </div>
    </section>
  );
};

export default PluginShowcase;
